import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import Axios from "utils/Axios";
import { useParams } from "react-router-dom";

const MAX_FILES = 100;
const MAX_RETRIES = 5;

const makeQueueItem = (file, indexOffset = 0) => ({
  id: `${file.name}-${file.size}-${file.lastModified}-${indexOffset}-${Math.random().toString(36).slice(2, 8)}`,
  file,
  previewUrl: URL.createObjectURL(file),
  status: "queued",
  attempts: 0,
  error: "",
});

export default function EventGalleryUploader({ heading = "Event Photo Gallery", description, showEventMeta = true }) {
  const [showAddImage, setShowAddImage] = useState(false);
  const [files, setFiles] = useState([]);
  const [images, setImages] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [rows, setRows] = useState(null);
  const [notice, setNotice] = useState("");
  const [uploadStats, setUploadStats] = useState({
    total: 0,
    completed: 0,
    failed: 0,
    activeFileName: "",
    currentFileProgress: 0,
    currentAttempt: 0,
  });

  const { id } = useParams();

  useEffect(() => {
    getEventMedia();
  }, [id]);

  useEffect(() => {
    if (showEventMeta) {
      getSingleEvent();
    }
  }, [id, showEventMeta]);

  useEffect(() => {
    return () => {
      files.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
  }, [files]);

  const progressValue = useMemo(() => {
    if (!uploadStats.total) return 0;

    const safeCompleted = Math.max(0, Math.min(uploadStats.completed, uploadStats.total));
    const safeCurrentProgress = Math.max(0, Math.min(uploadStats.currentFileProgress, 100));
    const fractional = safeCurrentProgress / 100;

    return Math.min(100, ((safeCompleted + fractional) / uploadStats.total) * 100);
  }, [uploadStats]);

  const completedFileCount = useMemo(() => {
    return Math.min(uploadStats.total, uploadStats.completed + (uploadStats.activeFileName ? 1 : 0));
  }, [uploadStats.activeFileName, uploadStats.completed, uploadStats.total]);

  const onChange = (e) => {
    const newFiles = e.target.files || e.dataTransfer.files;
    handleFiles(newFiles);
    if (e.target?.value !== undefined) {
      e.target.value = "";
    }
  };

  const handleFiles = (newFiles) => {
    const currentCount = files.length;
    const remainingSlots = Math.max(0, MAX_FILES - currentCount);
    const validFiles = Array.from(newFiles || []).filter((file) => file.type.startsWith("image/"));
    const acceptedFiles = validFiles.slice(0, remainingSlots);
    const rejectedCount = validFiles.length - acceptedFiles.length;

    if (acceptedFiles.length > 0) {
      setFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file, index) => makeQueueItem(file, prevFiles.length + index)),
      ]);
    }

    if (rejectedCount > 0 || validFiles.length !== Array.from(newFiles || []).length) {
      const messages = [];
      if (validFiles.length !== Array.from(newFiles || []).length) {
        messages.push("Some non-image files were skipped.");
      }
      if (rejectedCount > 0) {
        messages.push(`Only the first ${MAX_FILES} images can be queued at once.`);
      }
      setNotice(messages.join(" "));
    } else {
      setNotice("");
    }

    setDragging(false);
  };

  const removeFile = (fileId) => {
    if (loading) return;
    setFiles((prevFiles) => {
      const match = prevFiles.find((item) => item.id === fileId);
      if (match) {
        URL.revokeObjectURL(match.previewUrl);
      }
      return prevFiles.filter((item) => item.id !== fileId);
    });
  };

  const removeAllFiles = (force = false) => {
    if (loading && !force) return;
    files.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    setFiles([]);
  };

  const discard = (force = false) => {
    if (loading && !force) return;
    setShowAddImage(false);
    removeAllFiles(force);
    setNotice("");
    resetUploadStats();
  };

  const resetUploadStats = () => {
    setUploadStats({
      total: 0,
      completed: 0,
      failed: 0,
      activeFileName: "",
      currentFileProgress: 0,
      currentAttempt: 0,
    });
  };

  const setFileStatus = (fileId, patch) => {
    setFiles((prevFiles) => prevFiles.map((item) => (item.id === fileId ? { ...item, ...patch } : item)));
  };

  const uploadSingleFile = async (queueItem, attempt = 1) => {
    setFileStatus(queueItem.id, { status: attempt > 1 ? "retrying" : "uploading", attempts: attempt, error: "" });
    setUploadStats((prev) => ({
      ...prev,
      activeFileName: queueItem.file.name,
      currentFileProgress: 0,
      currentAttempt: attempt,
    }));

    const formData = new FormData();
    formData.append("type", "eventMedia");
    formData.append("event_id", id);
    formData.append("files[0]", queueItem.file);

    try {
      await Axios.post("", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || queueItem.file.size || 1;
          const percent = Math.round((progressEvent.loaded / total) * 100);
          setUploadStats((prev) => ({ ...prev, currentFileProgress: percent }));
        },
      });

      setFileStatus(queueItem.id, { status: "done", attempts: attempt, error: "" });
      setUploadStats((prev) => ({
        ...prev,
        completed: prev.completed + 1,
        currentFileProgress: 100,
      }));
      return true;
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.errors?.[0] ||
        error?.message ||
        "Upload failed";

      if (attempt < MAX_RETRIES) {
        setFileStatus(queueItem.id, {
          status: "retrying",
          attempts: attempt,
          error: `Retrying after failure (${attempt}/${MAX_RETRIES})`,
        });
        await new Promise((resolve) => setTimeout(resolve, Math.min(1000 * attempt, 4000)));
        return uploadSingleFile(queueItem, attempt + 1);
      }

      setFileStatus(queueItem.id, {
        status: "failed",
        attempts: attempt,
        error: errorMessage,
      });
      setUploadStats((prev) => ({ ...prev, failed: prev.failed + 1, currentFileProgress: 0 }));
      return false;
    }
  };

  const saveEventMedia = async () => {
    if (!files.length || loading) return;

    setLoading(true);
    setNotice("");
    setFiles((prevFiles) => prevFiles.map((item) => ({ ...item, status: "queued", attempts: 0, error: "" })));
    setUploadStats({
      total: files.length,
      completed: 0,
      failed: 0,
      activeFileName: "",
      currentFileProgress: 0,
      currentAttempt: 0,
    });

    let failedCount = 0;

    try {
      for (const queueItem of files) {
        // eslint-disable-next-line no-await-in-loop
        const success = await uploadSingleFile(queueItem);
        if (!success) {
          failedCount += 1;
        }
      }

      await getEventMedia();

      if (failedCount === 0) {
        discard(true);
      } else {
        setNotice(`${failedCount} image${failedCount === 1 ? "" : "s"} still failed after ${MAX_RETRIES} attempts. You can remove them or try again.`);
      }
    } finally {
      setLoading(false);
      setUploadStats((prev) => ({ ...prev, activeFileName: "", currentFileProgress: 0, currentAttempt: 0 }));
    }
  };

  const getEventMedia = async () => {
    try {
      const response = await Axios.get(`?type=eventMedia&event_id=${id}`);
      setImages(response.data || []);
    } catch (error) {
      console.error("Error fetching event media:", error);
    }
  };

  const toggleImageStatus = (imageId, currentStatus) => {
    setActionLoading(imageId);
    const newStatus = currentStatus === 1 ? 0 : 1;

    setImages((prevImages) => prevImages.map((img) => (img.id === imageId ? { ...img, status: newStatus } : img)));

    Axios.put(`?type=eventMedia&image_id=${imageId}&status=${newStatus}`)
      .catch((error) => {
        console.error("Error updating image status:", error);
        setImages((prevImages) => prevImages.map((img) => (img.id === imageId ? { ...img, status: currentStatus } : img)));
        alert("Failed to update image status. Please try again.");
      })
      .finally(() => {
        setActionLoading(null);
      });
  };

  const deleteImage = (imageId) => {
    setImageToDelete(imageId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!imageToDelete) return;

    setActionLoading(imageToDelete);
    setDeleteDialogOpen(false);

    Axios.delete(`?type=eventMedia&image_id=${imageToDelete}`)
      .then(() => {
        setImages((prevImages) => prevImages.filter((img) => img.id !== imageToDelete));
      })
      .catch((error) => {
        console.error("Error deleting image:", error);
        alert("Failed to delete image. Please try again.");
      })
      .finally(() => {
        setActionLoading(null);
        setImageToDelete(null);
      });
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setImageToDelete(null);
  };

  const imageUrl = (image) => {
    const parts = image.split("_");
    if (parts.length >= 3) {
      parts.splice(3, 0, "img_250");
      image = parts.join("_");
    }
    return `${process.env.REACT_APP_BASE_IMAGE_URL}gallery/${image}`;
  };

  const getSingleEvent = () => {
    Axios.get(`?type=event&id=${id}`)
      .then((response) => {
        setRows(response.data);
      })
      .catch((error) => {
        console.error("Failed to fetch events data", error);
      });
  };

  const statusLabel = (status) => {
    switch (status) {
      case "uploading":
        return "Uploading";
      case "retrying":
        return "Retrying";
      case "done":
        return "Done";
      case "failed":
        return "Failed";
      default:
        return "Queued";
    }
  };

  const statusClassName = (status) => {
    switch (status) {
      case "done":
        return "bg-green-100 text-green-700";
      case "failed":
        return "bg-red-100 text-red-700";
      case "uploading":
      case "retrying":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <div className="flex justify-between py-2.5 gap-4 flex-wrap">
        <div>
          {showEventMeta ? (
            <span className="itemValue text-2xl font-semibold">
              {rows?.title} {rows?.date ? `/ ${rows.date}` : ""}
            </span>
          ) : null}
          <div className="lg:mx-12 my-2">
            <div className="text-2xl py-0 text-primary">{heading}</div>
            <div className="text-sm font-light py-2">
              {description || "Add up to 100 event photos. Uploads run one by one with automatic retry if anything drops."}
            </div>
          </div>
        </div>
        <div className="flex items-center">
          <button
            onClick={() => {
              if (!loading) {
                setShowAddImage(!showAddImage);
              }
            }}
            type="button"
            className="focus:outline-none text-white bg-purple-700 hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-5 py-2.5 mb-2"
            disabled={loading}
          >
            {showAddImage ? "Close Uploader" : "Add New Images"}
          </button>
        </div>
      </div>

      {showAddImage ? (
        <div className="py-5 gallery-new-images">
          <div style={{ marginTop: "20px" }}>
            <div
              className={`dropZone ${dragging ? "dropZone-over" : ""}`}
              onDragEnter={() => setDragging(true)}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                onChange(e);
              }}
              onDragOver={(e) => e.preventDefault()}
            >
              <div className="dropZone-info">
                <InsertDriveFileIcon style={{ fontSize: "48px" }} />
                <br />
                <CloudUploadIcon className="dropZone-title" style={{ fontSize: "48px" }} />
                <span className="dropZone-title">
                  <strong>Drop images here or click to upload</strong>
                </span>
                <div className="dropZone-upload-limit-info">
                  <div>Queue up to {MAX_FILES} images at once. Uploads retry automatically if they fail.</div>
                </div>
              </div>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={onChange}
                style={{ display: "none" }}
                id="file-input"
                disabled={loading}
              />
              <label
                htmlFor="file-input"
                style={{
                  position: "absolute",
                  cursor: loading ? "not-allowed" : "pointer",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  opacity: 0,
                }}
              />
            </div>

            {notice ? (
              <Alert severity={notice.includes("failed") ? "warning" : "info"} sx={{ mt: 2 }}>
                {notice}
              </Alert>
            ) : null}

            {loading ? (
              <Box sx={{ mt: 3, p: 2, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                <div className="flex justify-between text-sm mb-2 gap-4 flex-wrap">
                  <span>
                    Uploading {completedFileCount} of {uploadStats.total}
                  </span>
                  <span>
                    {uploadStats.activeFileName}
                    {uploadStats.currentAttempt ? ` · attempt ${uploadStats.currentAttempt}/${MAX_RETRIES}` : ""}
                  </span>
                </div>
                <Box
                  sx={{
                    height: 10,
                    width: "100%",
                    borderRadius: 999,
                    backgroundColor: "#e5e7eb",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${progressValue}%`,
                      minWidth: progressValue > 0 ? "6px" : 0,
                      borderRadius: 999,
                      background: "linear-gradient(90deg, #7e22ce 0%, #a855f7 100%)",
                      transition: "width 0.25s ease",
                    }}
                  />
                </Box>
                <div className="flex justify-between text-xs mt-2 text-gray-600">
                  <span>{Math.round(progressValue)}% total</span>
                  <span>{uploadStats.completed} done / {uploadStats.failed} failed</span>
                </div>
              </Box>
            ) : null}

            {files.length > 0 ? (
              <div style={{ marginTop: "20px" }}>
                <div className="flex justify-between items-center gap-4 flex-wrap mb-3">
                  <div className="text-sm text-gray-600">{files.length} / {MAX_FILES} images queued</div>
                  <button
                    onClick={removeAllFiles}
                    type="button"
                    className="focus:outline-none text-white bg-purple-700 hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-5 py-2.5 mb-2"
                    disabled={loading}
                  >
                    Remove All Images
                  </button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", marginTop: "10px" }}>
                  {files.map((queueItem) => (
                    <div key={queueItem.id} style={{ margin: "10px", position: "relative", width: "120px" }}>
                      <img
                        src={queueItem.previewUrl}
                        alt={queueItem.file.name}
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid #e5e7eb",
                        }}
                      />
                      {!loading ? (
                        <button
                          type="button"
                          onClick={() => removeFile(queueItem.id)}
                          className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1"
                          title="Remove image"
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </button>
                      ) : null}
                      <div className={`mt-2 text-xs px-2 py-1 rounded-full inline-block ${statusClassName(queueItem.status)}`}>
                        {statusLabel(queueItem.status)}
                      </div>
                      <div className="text-xs text-gray-600 mt-1 break-words">{queueItem.file.name}</div>
                      {queueItem.error ? <div className="text-xs text-red-600 mt-1">{queueItem.error}</div> : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <div className="flex justify-end py-2.5">
            <button
              type="button"
              onClick={discard}
              className="text-purple-700 hover:text-white border border-purple-700 hover:bg-purple-800 focus:ring-4 focus:outline-none focus:ring-purple-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2"
              disabled={loading}
            >
              Discard
            </button>
            <button
              onClick={saveEventMedia}
              type="button"
              className="focus:outline-none text-white bg-purple-700 hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-5 py-2.5 mb-2"
              disabled={loading || files.length === 0}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : `Upload ${files.length || ""} Image${files.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      ) : (
        ""
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((col) => (
          <div key={col} className="grid gap-4">
            {images
              .filter((_, index) => index % 4 === col)
              .map((image, index) => (
                <div key={image.id || index} className="relative group">
                  <img
                    className={`h-auto max-w-full rounded-lg ${image.status === 0 ? "opacity-50" : ""}`}
                    src={imageUrl(image.title)}
                    alt={image.title}
                  />
                  <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity right-2 flex space-x-2 z-10 pointer-events-none">
                    {actionLoading === image.id ? (
                      <CircularProgress size={24} color="inherit" className="text-white" />
                    ) : (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleImageStatus(image.id, image.status);
                          }}
                          className="px-1.5 bg-white rounded-full hover:bg-gray-200 text-gray-700 cursor-pointer pointer-events-auto"
                          title={image.status === 1 ? "Disable image" : "Enable image"}
                        >
                          {image.status === 1 ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteImage(image.id);
                          }}
                          className="px-1.5 bg-white rounded-full hover:bg-gray-200 text-red-600 cursor-pointer pointer-events-auto"
                          title="Delete image"
                        >
                          <DeleteIcon fontSize="small" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
          </div>
        ))}
      </div>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this image? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
